// ─────────────────────────────────────────────────────────────────────────
// Finanzas (27-ago-2026) — réplica automática de la planilla "RESULTADOS
// MENSUALES" de Zayda: resultado de CAJA mensual = VENTA − FIJOS − VARIABLES.
// Los datos salen solos del ERP (facturas SII, liquidaciones, rendiciones);
// acá solo se administran los gastos fijos, los pasivos financieros y los
// retiros de socios (columna CASA — separada, no resta utilidad).
// Vista "Histórico": la serie 2020→hoy (planilla sembrada + años ERP).
// Componente standalone: lo monta el orquestador como pestaña de Contabilidad.
// ─────────────────────────────────────────────────────────────────────────
import React, { useEffect, useState } from "react";
import backend from "../../backend";
import useIsMobile from "../../hooks/useIsMobile";
import ConfirmModal from "../../components/design-system/ConfirmModal";
import { formatPrice } from "../../utils";

const fmt = n => n === null || n === undefined ? "—" : `${n < 0 ? "−" : ""}$${formatPrice(Math.round(Math.abs(n || 0)))}`;
const fmtD = d => d ? new Date(d).toLocaleDateString("es-CL", { day: "2-digit", month: "short", year: "2-digit" }) : "—";
const num = { fontVariantNumeric: "tabular-nums" };
const MESES = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
const colorU = u => u > 0 ? "#2ecc71" : u < 0 ? "#e74c3c" : "#8a8a8a";

const CATEGORIAS_GF = ["arriendo", "prestamo", "seguridad", "servicios", "seguros", "tag", "banco", "otros"];
const TIPOS_PASIVO = [["prestamo", "Préstamo"], ["linea_credito", "Línea de crédito"], ["leasing", "Leasing"]];

const S = {
  card:  { background: "#232323", border: "1px solid #2e2e2e", borderRadius: 12, padding: 14 },
  label: { fontSize: 10, fontWeight: 700, color: "#8a8a8a", textTransform: "uppercase", letterSpacing: ".05em" },
  th: {
    background: "#1f1f1f", padding: "8px 12px", textAlign: "left", fontSize: 10,
    fontWeight: 700, color: "#999", borderBottom: "1px solid #2e2e2e",
    textTransform: "uppercase", letterSpacing: ".04em", whiteSpace: "nowrap",
  },
  td: {
    padding: "8px 12px", color: "#bbb", borderBottom: "1px solid #252525",
    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
  },
  input: {
    background: "#1a1a1a", border: "1px solid #333", borderRadius: 6, color: "#eee",
    padding: "7px 10px", fontSize: 12, outline: "none", minWidth: 0,
  },
  btn: {
    background: "#3a1114", color: "#ff8a8f", border: "1px solid #e30613",
    borderRadius: 6, padding: "7px 14px", fontSize: 11, fontWeight: 700, cursor: "pointer",
  },
  btnGris: {
    background: "#2a2a2a", color: "#999", border: "1px solid #3a3a3a",
    borderRadius: 6, padding: "4px 10px", fontSize: 11, fontWeight: 700, cursor: "pointer",
  },
  chip: activo => ({
    background: activo ? "#3a1114" : "#1e1e1e", color: activo ? "#ff8a8f" : "#999",
    border: `1px solid ${activo ? "#e30613" : "#333"}`,
    borderRadius: 6, padding: "5px 12px", fontSize: 11, cursor: "pointer",
  }),
};

// ── Sección colapsable de administración ────────────────────────────────
const Seccion = ({ titulo, resumen, abierta, onToggle, children }) => (
  <div style={{ ...S.card, padding: 0, overflow: "hidden" }}>
    <div onClick={onToggle}
      style={{ padding: "12px 14px", cursor: "pointer", display: "flex", alignItems: "center", gap: 8 }}>
      <span style={{ color: "#8a8a8a", fontSize: 11, width: 12 }}>{abierta ? "▾" : "▸"}</span>
      <span style={{ fontSize: 12, fontWeight: 700, color: "#fff", flex: 1 }}>{titulo}</span>
      <span style={{ fontSize: 11, color: "#8a8a8a", ...num }}>{resumen}</span>
    </div>
    {abierta && <div style={{ padding: "0 14px 14px", borderTop: "1px solid #2a2a2a" }}>{children}</div>}
  </div>
);

const Finanzas = () => {
  const isMobile = useIsMobile();
  const anoActual = new Date().getFullYear();
  const [vista, setVista] = useState("ano");       // ano | historico
  const [ano, setAno] = useState(anoActual);
  const [data, setData] = useState(null);          // /resultado
  const [historico, setHistorico] = useState(null);
  const [gastos, setGastos] = useState([]);
  const [pasivos, setPasivos] = useState([]);
  const [retiros, setRetiros] = useState([]);
  const [error, setError] = useState(null);
  const [abierta, setAbierta] = useState(null);    // gastos | pasivos | retiros
  const [ocupado, setOcupado] = useState(false);
  // ConfirmModal del design system (28-ago, adiós window.confirm):
  // { titulo, descripcion, label, variant, run: async () => void }
  const [confirmar, setConfirmar] = useState(null);

  // Formularios de alta (colapsados dentro de cada sección)
  const [fGasto, setFGasto] = useState({ concepto: "", categoria: "otros", monto: "", diaPago: "" });
  const [fPasivo, setFPasivo] = useState({ nombre: "", tipo: "prestamo", institucion: "", cuotaMensual: "", saldoActual: "", cuotasRestantes: "" });
  const [fRetiro, setFRetiro] = useState({ fecha: "", monto: "", concepto: "", beneficiario: "" });

  const cargar = async (a = ano) => {
    try {
      const [r, g, p, rt] = await Promise.all([
        backend.get("finanzas/resultado", { params: { ano: a } }),
        backend.get("finanzas/gastos-fijos"),
        backend.get("finanzas/pasivos"),
        backend.get("finanzas/retiros", { params: { ano: a } }),
      ]);
      setData(r.data); setGastos(g.data); setPasivos(p.data); setRetiros(rt.data);
      setError(null);
    } catch (e) { setError(e?.response?.data?.error || e.message); }
  };
  useEffect(() => { cargar(ano); }, [ano]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (vista !== "historico" || historico) return;
    backend.get("finanzas/historico")
      .then(r => setHistorico(r.data))
      .catch(e => setError(e?.response?.data?.error || e.message));
  }, [vista, historico]);

  const accion = async fn => {
    setOcupado(true);
    try { await fn(); setHistorico(null); await cargar(); }
    catch (e) { alert(e?.response?.data?.error || e.message); }
    setOcupado(false);
  };

  if (error) return <div style={{ color: "#e74c3c", fontSize: 12, padding: 12 }}>Error: {error}</div>;
  if (!data) return <div style={{ color: "#8a8a8a", fontSize: 12, padding: 12 }}>Calculando resultados…</div>;

  const { meses, resumen } = data;

  // "Deuda financiera" y "Retiros" abren su sección de administración (28-ago)
  const kpis = [
    { label: `Utilidad acumulada ${ano}`, valor: resumen.utilidadAcumulada, color: colorU(resumen.utilidadAcumulada) },
    { label: "Punto de equilibrio mensual", valor: resumen.puntoEquilibrioMensual, color: "#f39c12" },
    { label: "Deuda financiera total", valor: resumen.deudaTotal, color: "#3498db", seccion: "pasivos" },
    { label: `Retiros ${ano} (casa)`, valor: resumen.retirosAno, color: "#9b59b6", seccion: "retiros" },
  ];

  // ── Fila / tarjeta de mes ─────────────────────────────────────────────
  const filaMes = (m, i) => {
    const dim = m.futuro ? { opacity: 0.4 } : {};
    if (isMobile) return (
      <div key={m.mes} style={{ ...S.card, padding: "10px 14px", ...dim }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: "#fff" }}>
            {MESES[i]}{m.sinSueldos && !m.futuro ? <span title="Sin liquidaciones este mes" style={{ color: "#f39c12", fontSize: 10 }}> · sin sueldos</span> : null}
          </span>
          <span style={{ fontSize: 14, fontWeight: 800, color: colorU(m.utilidad), ...num }}>{fmt(m.utilidad)}</span>
        </div>
        <div style={{ fontSize: 11, color: "#8a8a8a", marginTop: 4, ...num }}>
          Venta {fmt(m.venta)} · Fijos {fmt(m.fijos)} · Variables {fmt(m.variables)}
          {m.retiros > 0 && <span style={{ color: "#9b59b6" }}> · Casa {fmt(m.retiros)}</span>}
        </div>
      </div>
    );
    return (
      <tr key={m.mes} style={{ background: i % 2 === 0 ? "#232323" : "#212121", ...dim }}>
        <td style={{ ...S.td, color: "#fff", fontWeight: 700 }}>
          {MESES[i]}
          {m.sinSueldos && !m.futuro && <span title="Sin liquidaciones este mes" style={{ color: "#f39c12", fontSize: 10 }}> ⚠ sin sueldos</span>}
        </td>
        <td style={{ ...S.td, textAlign: "right", ...num }}>{fmt(m.fijos)}</td>
        <td style={{ ...S.td, textAlign: "right", ...num }}>{fmt(m.variables)}</td>
        <td style={{ ...S.td, textAlign: "right", color: "#fff", ...num }}>{fmt(m.venta)}</td>
        <td style={{ ...S.td, textAlign: "right", fontWeight: 800, color: colorU(m.utilidad), ...num }}>{fmt(m.utilidad)}</td>
        <td style={{ ...S.td, textAlign: "right", color: m.retiros ? "#9b59b6" : "#555", ...num }}>{fmt(m.retiros)}</td>
      </tr>
    );
  };

  // ── Vista histórico ───────────────────────────────────────────────────
  const vistaHistorico = () => {
    if (!historico) return <div style={{ color: "#8a8a8a", fontSize: 12, padding: 12 }}>Cargando histórico…</div>;
    const anos = historico.anos;
    const maxU = Math.max(1, ...anos.map(a => Math.abs(a.utilidad || 0)));
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {/* Barras CSS de utilidad por año */}
        <div style={S.card}>
          <div style={{ ...S.label, marginBottom: 10 }}>Utilidad por año</div>
          {anos.map(a => (
            <div key={a.ano} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
              <span style={{ width: 60, fontSize: 11, fontWeight: 700, color: "#fff", ...num }}>
                {a.ano}{a.parcial ? "*" : ""}
              </span>
              <div style={{ flex: 1, background: "#1a1a1a", borderRadius: 4, height: 16, overflow: "hidden" }}>
                <div style={{
                  width: `${Math.round(Math.abs(a.utilidad || 0) / maxU * 100)}%`,
                  height: "100%", borderRadius: 4, minWidth: a.utilidad ? 2 : 0,
                  background: (a.utilidad || 0) >= 0 ? "#2ecc71" : "#e74c3c",
                }} />
              </div>
              <span style={{ width: isMobile ? 90 : 110, textAlign: "right", fontSize: 11, fontWeight: 800, color: colorU(a.utilidad || 0), ...num }}>
                {fmt(a.utilidad)}
              </span>
            </div>
          ))}
          <div style={{ fontSize: 10, color: "#8a8a8a", marginTop: 6 }}>* año en curso: acumulado a la fecha</div>
        </div>

        {/* Tabla / tarjetas por año */}
        {isMobile ? anos.map(a => (
          <div key={a.ano} style={{ ...S.card, padding: "10px 14px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: "#fff" }}>{a.ano}{a.parcial ? "*" : ""}
                <span style={{ fontSize: 9, color: "#8a8a8a", fontWeight: 400 }}> · {a.fuente === "erp" ? "ERP" : "planilla Zayda"}</span>
              </span>
              <span style={{ fontSize: 14, fontWeight: 800, color: colorU(a.utilidad || 0), ...num }}>{fmt(a.utilidad)}</span>
            </div>
            <div style={{ fontSize: 11, color: "#8a8a8a", marginTop: 4, ...num }}>
              Venta {fmt(a.venta)} · Gastos {fmt(a.gastos)} · <span style={{ color: "#9b59b6" }}>Casa {fmt(a.casa)}</span>
            </div>
          </div>
        )) : (
          <div style={{ border: "1px solid #2e2e2e", borderRadius: 8, overflowX: "auto", background: "#232323" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, tableLayout: "fixed" }}>
              <thead>
                <tr>
                  <th style={S.th}>Año</th>
                  <th style={{ ...S.th, textAlign: "right" }}>Venta</th>
                  <th style={{ ...S.th, textAlign: "right" }}>Gastos</th>
                  <th style={{ ...S.th, textAlign: "right" }}>Utilidad</th>
                  <th style={{ ...S.th, textAlign: "right" }}>Casa (retiros)</th>
                  <th style={S.th}>Fuente</th>
                </tr>
              </thead>
              <tbody>
                {anos.map((a, i) => (
                  <tr key={a.ano} style={{ background: i % 2 === 0 ? "#232323" : "#212121" }}>
                    <td style={{ ...S.td, color: "#fff", fontWeight: 700 }}>{a.ano}{a.parcial ? "*" : ""}</td>
                    <td style={{ ...S.td, textAlign: "right", ...num }}>{fmt(a.venta)}</td>
                    <td style={{ ...S.td, textAlign: "right", ...num }}>{fmt(a.gastos)}</td>
                    <td style={{ ...S.td, textAlign: "right", fontWeight: 800, color: colorU(a.utilidad || 0), ...num }}>{fmt(a.utilidad)}</td>
                    <td style={{ ...S.td, textAlign: "right", color: "#9b59b6", ...num }}>{fmt(a.casa)}</td>
                    <td style={{ ...S.td }}>
                      <span style={{ fontSize: 10, fontWeight: 700, padding: "1px 8px", borderRadius: 20,
                        color: a.fuente === "erp" ? "#3498db" : "#8a8a8a",
                        background: a.fuente === "erp" ? "#0d2035" : "#222" }}>
                        {a.fuente === "erp" ? "ERP" : "planilla Zayda"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  // ── Render principal ──────────────────────────────────────────────────
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {/* Selector de vista + año */}
      <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
        <button type="button" className="chip-touch no-touch-min t-02" style={S.chip(vista === "ano")} onClick={() => setVista("ano")}>Año</button>
        <button type="button" className="chip-touch no-touch-min t-02" style={S.chip(vista === "historico")} onClick={() => setVista("historico")}>Histórico 2020→hoy</button>
        {vista === "ano" && (
          <div style={{ display: "flex", alignItems: "center", gap: 4, marginLeft: "auto" }}>
            <button type="button" style={S.btnGris} onClick={() => setAno(a => a - 1)}>◂</button>
            <span style={{ fontSize: 13, fontWeight: 800, color: "#fff", minWidth: 44, textAlign: "center", ...num }}>{ano}</span>
            <button type="button" style={{ ...S.btnGris, opacity: ano >= anoActual ? 0.4 : 1 }}
              disabled={ano >= anoActual} onClick={() => setAno(a => a + 1)}>▸</button>
          </div>
        )}
      </div>

      {vista === "historico" ? vistaHistorico() : (
        <>
          {/* Tarjetas */}
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {kpis.map(k => (
              <div key={k.label}
                onClick={k.seccion ? () => setAbierta(abierta === k.seccion ? null : k.seccion) : undefined}
                className={k.seccion ? "t-02 tap-card" : undefined}
                style={{ ...S.card, flex: isMobile ? "1 1 42%" : "1 1 0", minWidth: 150,
                  cursor: k.seccion ? "pointer" : undefined,
                  border: `1px solid ${k.seccion && abierta === k.seccion ? k.color : "#2e2e2e"}` }}>
                <div style={S.label}>{k.label}{k.seccion ? " ›" : ""}</div>
                <div style={{ fontSize: 20, fontWeight: 800, color: k.color, ...num }}>{fmt(k.valor)}</div>
              </div>
            ))}
          </div>

          {/* Tabla mensual */}
          {isMobile ? meses.map(filaMes) : (
            <div style={{ border: "1px solid #2e2e2e", borderRadius: 8, overflowX: "auto", background: "#232323" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, tableLayout: "fixed" }}>
                <thead>
                  <tr>
                    <th style={S.th}>Mes</th>
                    <th style={{ ...S.th, textAlign: "right" }}>Fijos</th>
                    <th style={{ ...S.th, textAlign: "right" }}>Variables</th>
                    <th style={{ ...S.th, textAlign: "right" }}>Venta</th>
                    <th style={{ ...S.th, textAlign: "right" }}>Utilidad</th>
                    <th style={{ ...S.th, textAlign: "right" }}>Retiros</th>
                  </tr>
                </thead>
                <tbody>{meses.map(filaMes)}</tbody>
              </table>
            </div>
          )}
          <div style={{ fontSize: 10, color: "#8a8a8a" }}>
            Fijos = gastos fijos + sueldos (liquidaciones) + cuotas de préstamos · Variables = compras SII + rendiciones sin factura ·
            Retiros (casa) van separados, no restan utilidad · Punto de equilibrio = fijos + variables promedio del año
          </div>

          {/* ── Gastos fijos ── */}
          <Seccion titulo="Gastos fijos mensuales" abierta={abierta === "gastos"}
            onToggle={() => setAbierta(abierta === "gastos" ? null : "gastos")}
            resumen={`${gastos.filter(g => g.activo && !g.hasta).length} vigentes`}>
            {gastos.map(g => (
              <div key={g._id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 0", borderBottom: "1px solid #2a2a2a", flexWrap: "wrap" }}>
                <div style={{ flex: 1, minWidth: 160 }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: g.hasta || !g.activo ? "#777" : "#fff" }}>{g.concepto}</span>
                  <span style={{ fontSize: 10, color: "#8a8a8a" }}> · {g.categoria} · día {g.diaPago}{g.hasta ? ` · terminó ${fmtD(g.hasta)}` : ""}{!g.activo ? " · inactivo" : ""}</span>
                </div>
                <span style={{ fontSize: 12, fontWeight: 800, color: "#fff", ...num }}>{fmt(g.monto)}/mes</span>
                {!g.hasta && g.activo && (
                  <button type="button" style={S.btnGris} disabled={ocupado}
                    onClick={() => setConfirmar({
                      titulo: `Terminar "${g.concepto}"`,
                      descripcion: `Termina hoy y deja de sumar a los fijos desde el próximo mes (${fmt(g.monto)}/mes).`,
                      label: "Terminar",
                      variant: "danger",
                      run: () => accion(() => backend.patch(`finanzas/gastos-fijos/${g._id}`, { hasta: new Date().toISOString() })),
                    })}>
                    Terminar
                  </button>
                )}
              </div>
            ))}
            <div style={{ display: "flex", gap: 6, marginTop: 10, flexWrap: "wrap" }}>
              <input style={{ ...S.input, flex: 2, minWidth: 140 }} placeholder="Concepto (ej. Arriendo galpón)"
                value={fGasto.concepto} onChange={e => setFGasto({ ...fGasto, concepto: e.target.value })} />
              <select style={{ ...S.input, flex: 1 }} value={fGasto.categoria}
                onChange={e => setFGasto({ ...fGasto, categoria: e.target.value })}>
                {CATEGORIAS_GF.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              <input style={{ ...S.input, width: 110 }} placeholder="Monto/mes" type="number"
                value={fGasto.monto} onChange={e => setFGasto({ ...fGasto, monto: e.target.value })} />
              <input style={{ ...S.input, width: 80 }} placeholder="Día pago" type="number"
                value={fGasto.diaPago} onChange={e => setFGasto({ ...fGasto, diaPago: e.target.value })} />
              <button type="button" style={S.btn} disabled={ocupado || !fGasto.concepto || !fGasto.monto}
                onClick={() => accion(async () => {
                  await backend.post("finanzas/gastos-fijos", { ...fGasto, monto: Number(fGasto.monto), diaPago: Number(fGasto.diaPago) || 1, desde: new Date().toISOString() });
                  setFGasto({ concepto: "", categoria: "otros", monto: "", diaPago: "" });
                })}>+ Agregar</button>
            </div>
          </Seccion>

          {/* ── Pasivos financieros ── */}
          <Seccion titulo="Préstamos y líneas de crédito" abierta={abierta === "pasivos"}
            onToggle={() => setAbierta(abierta === "pasivos" ? null : "pasivos")}
            resumen={`deuda ${fmt(resumen.deudaTotal)} · cuotas ${fmt(resumen.cuotaMensualPasivos)}/mes`}>
            {pasivos.map(p => (
              <div key={p._id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 0", borderBottom: "1px solid #2a2a2a", flexWrap: "wrap" }}>
                <div style={{ flex: 1, minWidth: 180 }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: p.estado === "pagado" ? "#777" : "#fff" }}>{p.nombre}</span>
                  <span style={{ fontSize: 10, color: "#8a8a8a" }}> · {p.institucion || p.tipo}{p.cuotasRestantes ? ` · quedan ${p.cuotasRestantes} cuotas` : ""}</span>
                </div>
                <span style={{ fontSize: 11, color: "#8a8a8a", ...num }}>cuota {fmt(p.cuotaMensual)}</span>
                <span style={{ fontSize: 12, fontWeight: 800, color: p.estado === "pagado" ? "#2ecc71" : "#3498db", ...num }}>
                  {p.estado === "pagado" ? "PAGADO" : `saldo ${fmt(p.saldoActual)}`}
                </span>
                {p.estado === "activo" && (
                  <button type="button" style={S.btnGris} disabled={ocupado}
                    onClick={() => setConfirmar({
                      titulo: `Marcar "${p.nombre}" como pagado`,
                      descripcion: "El saldo queda en $0 y la cuota mensual deja de sumar a los gastos fijos.",
                      label: "Marcar pagado",
                      variant: "success",
                      run: () => accion(() => backend.patch(`finanzas/pasivos/${p._id}`, { estado: "pagado", saldoActual: 0, cuotasRestantes: 0 })),
                    })}>
                    Marcar pagado
                  </button>
                )}
              </div>
            ))}
            <div style={{ display: "flex", gap: 6, marginTop: 10, flexWrap: "wrap" }}>
              <input style={{ ...S.input, flex: 2, minWidth: 150 }} placeholder="Nombre (ej. Préstamo máquina)"
                value={fPasivo.nombre} onChange={e => setFPasivo({ ...fPasivo, nombre: e.target.value })} />
              <select style={{ ...S.input }} value={fPasivo.tipo} onChange={e => setFPasivo({ ...fPasivo, tipo: e.target.value })}>
                {TIPOS_PASIVO.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
              <input style={{ ...S.input, width: 110 }} placeholder="Institución"
                value={fPasivo.institucion} onChange={e => setFPasivo({ ...fPasivo, institucion: e.target.value })} />
              <input style={{ ...S.input, width: 105 }} placeholder="Cuota/mes" type="number"
                value={fPasivo.cuotaMensual} onChange={e => setFPasivo({ ...fPasivo, cuotaMensual: e.target.value })} />
              <input style={{ ...S.input, width: 110 }} placeholder="Saldo total" type="number"
                value={fPasivo.saldoActual} onChange={e => setFPasivo({ ...fPasivo, saldoActual: e.target.value })} />
              <input style={{ ...S.input, width: 80 }} placeholder="Cuotas" type="number"
                value={fPasivo.cuotasRestantes} onChange={e => setFPasivo({ ...fPasivo, cuotasRestantes: e.target.value })} />
              <button type="button" style={S.btn} disabled={ocupado || !fPasivo.nombre}
                onClick={() => accion(async () => {
                  await backend.post("finanzas/pasivos", {
                    ...fPasivo, cuotaMensual: Number(fPasivo.cuotaMensual) || 0,
                    saldoActual: Number(fPasivo.saldoActual) || 0, cuotasRestantes: Number(fPasivo.cuotasRestantes) || 0,
                  });
                  setFPasivo({ nombre: "", tipo: "prestamo", institucion: "", cuotaMensual: "", saldoActual: "", cuotasRestantes: "" });
                })}>+ Agregar</button>
            </div>
          </Seccion>

          {/* ── Retiros de socios (casa) ── */}
          <Seccion titulo="Retiros de socios (casa)" abierta={abierta === "retiros"}
            onToggle={() => setAbierta(abierta === "retiros" ? null : "retiros")}
            resumen={`${fmt(resumen.retirosAno)} en ${ano}`}>
            <div style={{ fontSize: 10, color: "#8a8a8a", margin: "8px 0" }}>
              Gastos de casa/familia pagados por la empresa. Van SEPARADOS: no restan a la utilidad del negocio.
            </div>
            {retiros.map(r => (
              <div key={r._id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 0", borderBottom: "1px solid #2a2a2a", flexWrap: "wrap" }}>
                <span style={{ fontSize: 11, color: "#8a8a8a", width: 70, ...num }}>{fmtD(r.fecha)}</span>
                <div style={{ flex: 1, minWidth: 140 }}>
                  <span style={{ fontSize: 12, color: "#fff" }}>{r.concepto || "(sin concepto)"}</span>
                  {r.beneficiario && <span style={{ fontSize: 10, color: "#8a8a8a" }}> · {r.beneficiario}</span>}
                </div>
                <span style={{ fontSize: 12, fontWeight: 800, color: "#9b59b6", ...num }}>{fmt(r.monto)}</span>
                <button type="button" style={S.btnGris} disabled={ocupado}
                  onClick={() => setConfirmar({
                    titulo: "Eliminar retiro",
                    descripcion: `${r.concepto || "(sin concepto)"} · ${fmt(r.monto)} · ${fmtD(r.fecha)}`,
                    label: "Eliminar",
                    variant: "danger",
                    run: () => accion(() => backend.delete(`finanzas/retiros/${r._id}`)),
                  })}>✕</button>
              </div>
            ))}
            <div style={{ display: "flex", gap: 6, marginTop: 10, flexWrap: "wrap" }}>
              <input style={{ ...S.input, width: 130 }} type="date"
                value={fRetiro.fecha} onChange={e => setFRetiro({ ...fRetiro, fecha: e.target.value })} />
              <input style={{ ...S.input, width: 110 }} placeholder="Monto" type="number"
                value={fRetiro.monto} onChange={e => setFRetiro({ ...fRetiro, monto: e.target.value })} />
              <input style={{ ...S.input, flex: 1, minWidth: 130 }} placeholder="Concepto"
                value={fRetiro.concepto} onChange={e => setFRetiro({ ...fRetiro, concepto: e.target.value })} />
              <input style={{ ...S.input, width: 120 }} placeholder="Beneficiario"
                value={fRetiro.beneficiario} onChange={e => setFRetiro({ ...fRetiro, beneficiario: e.target.value })} />
              <button type="button" style={S.btn} disabled={ocupado || !fRetiro.monto}
                onClick={() => accion(async () => {
                  await backend.post("finanzas/retiros", { ...fRetiro, monto: Number(fRetiro.monto), fecha: fRetiro.fecha || undefined });
                  setFRetiro({ fecha: "", monto: "", concepto: "", beneficiario: "" });
                })}>+ Agregar</button>
            </div>
          </Seccion>
        </>
      )}

      {/* ── ConfirmModal del design system (28-ago) ── */}
      <ConfirmModal
        open={!!confirmar}
        title={confirmar?.titulo}
        description={confirmar?.descripcion}
        confirmLabel={confirmar?.label}
        confirmVariant={confirmar?.variant || "primary"}
        onCancel={() => setConfirmar(null)}
        onConfirm={async () => {
          const c = confirmar;
          setConfirmar(null);
          if (c?.run) await c.run();
        }}
      />
    </div>
  );
};

export default Finanzas;
